import { BaseServerConnector, ProtocolArray } from './BaseServerConnector.js';
import { ConnectorServerConfig } from '../servers/ConnectorServer.js';
import * as dgram from 'dgram';
import { PROTOCOLS } from '@gotti/core';

export type UdpClientOptions = {
  host: string;
  receivedAcks: number[];
  pendingAcks: {[ack: number]: {
    message: string,
    seq?: number,
    interval?: any,
  }},
  noPingReceivedTimeout?: NodeJS.Timeout;
  pingTimeout?: NodeJS.Timeout;
  ackTimeout?: NodeJS.Timeout;
  receivedOutOfOrderSeqs: {[seq: number]: ProtocolArray };
  port: number;
  lastAck: number;
  lastSeq: number;
  lastSentPing: number;
  lastReceivedPing: number;
  lastProcessedSeq: number;
  messageHandler?: (msg: ProtocolArray) => void;
}

export enum UDP_PROTOCOL {
}

export class UDPServerConnector extends BaseServerConnector<UdpClientOptions> {
  private server!: dgram.Socket;

  constructor(c: ConnectorServerConfig) {
    super(c);
  }

  public start() {
    this.server = dgram.createSocket('udp4');

    this.server.on('error', (err) => {
      console.error(`UDP server error: ${err.message}`);
    })

    this.server.on('message', (msg: Buffer, rinfo: dgram.RemoteInfo) => {
     // console.log('SERVER UDP MESSAGE ON MESSAGE!!')
      try {
        const { address: host, port } = rinfo;
        const message = msg.toString();
       // console.log('message was', message);
        const payload = JSON.parse(message);
        let privateId = payload.pop();
        let ack = 0;
        let seq = 0;
        if(typeof privateId === 'number') {
          seq = privateId;
          privateId = payload.pop();
          if(typeof privateId === 'number') {
            ack = privateId;
            privateId = payload.pop();
          } else {
            ack = seq;
          }
        }
        // console.log({  privateId, })

        const client = this.clientsByPrivateId[privateId];
        // console.log({ privateId, publicId: payload[0] })
        if(!client) {
          const publicId = payload[0];
          if(this._onValidateClient(privateId, publicId)) {
            const send = (msg: string) => {
              //console.log('finna send', msg)
              this.server.send(msg, port, host, (err) => {
                if(err) {
                  console.error(`Error sending message to client: ${privateId}: ${err.message}`);
                }
              });
            }
            
            const options : UdpClientOptions = {
              host,
              port,
              lastAck: 0,
              lastSeq: 0,
              lastProcessedSeq: 0,
              receivedAcks: [],
              pendingAcks: {},
              receivedOutOfOrderSeqs: {},
              lastSentPing: 0,
              lastReceivedPing: 0,
            }
            
            const ackTimeout = () => {
              if(options.receivedAcks.length !== 0) {
                send(JSON.stringify([
                  PROTOCOLS.SERVER_ACK,
                  ...options.receivedAcks
                ]));
              }
              options.ackTimeout = setTimeout(ackTimeout, 500);
            }

            options.ackTimeout = setTimeout(ackTimeout, 500);
            
            const pingTimeout = () => {
              send(JSON.stringify([PROTOCOLS.PING, options.lastReceivedPing+1]))
              options.pingTimeout = setTimeout(pingTimeout, 1000);
            }
           
            const createdClient = {
              options,
              gotFirstAreas: false,
              reqSeq: 0,
              pendingReqs: {},
              disconnect: () => {
                // console.log('DISCONNECTING CLIENT', privateId);
                clearTimeout(options.ackTimeout);
                clearTimeout(options.pingTimeout);
                //console.log('CLEARED PING TIMEOUT FOR ', privateId)
                clearTimeout(options.noPingReceivedTimeout);
                delete options.ackTimeout;
                const pendingAcks = Object.keys(options.pendingAcks);
                pendingAcks.forEach((ackStr) => {
                  const ack = parseInt(ackStr, 10);
                  clearInterval(options.pendingAcks[ack]?.interval);
                  delete options.pendingAcks[ack];
                })
                //send disconnect message
              },
              privateId,
              publicId,
              onMessage: (cb: (msg: ProtocolArray) => void) => {
                options.messageHandler = cb;
              },
              send: (msg: string, reliable=false, ordered=false) => {
                if (reliable) {
                  const ackId = ++options.lastAck;
                  if(ordered) {
                    const seq = ++options.lastSeq;
                    msg = JSON.stringify([msg, ackId, seq]);
                    options.pendingAcks[ackId] = {
                      message: msg,
                      seq,
                    };
                  } else {
                    msg = JSON.stringify([msg, ackId]);
                    options.pendingAcks[ackId] = {
                      message: msg,
                    };
                  }
                  options.pendingAcks[ackId].interval = setInterval(() => {
                    if (options.pendingAcks[ackId]) {
                      send(msg)
                    }
                  }, 500); // Retransmission interval
                } else {
                  msg = JSON.stringify([msg])
                }
                send(msg)
              }
            }

            this.addClient(createdClient);
            options.noPingReceivedTimeout = setTimeout(() => {
              createdClient.disconnect();
            }, this.config.options.clientTimeout || 10000);

            pingTimeout();
          }
        } else if(host === client.options.host) {
          // console.log('PAYLOADPROTOCOL:', payload[0])
          if(payload[0] === PROTOCOLS.CLIENT_ACK) {
            const confirmAcks = [];
            for(let i = 1; i < payload.length; i++) {
              const ackId = payload[i] as number;
              confirmAcks.push(ackId);
              if(client.options.pendingAcks[ackId]) {
                clearInterval(client.options.pendingAcks[ackId].interval);
                delete client.options.pendingAcks[payload[i]];
              }
              const nextPayload = JSON.stringify([PROTOCOLS.CLIENT_ACK, ...confirmAcks])
              client.send(nextPayload);
            }
          } else if (payload[0] === PROTOCOLS.SERVER_ACK) {
            for(let i = 1; i < payload.length; i++) {
              const idx = client.options.receivedAcks.indexOf(payload[i]);
              if(idx > -1) {
                client.options.receivedAcks.splice(idx, 1);
              }
            }
          } else if (payload[0] === PROTOCOLS.PING) {
            const ping = payload[1] as number;
            if(ping === client.options.lastReceivedPing + 1) {
              client.options.lastReceivedPing = ping;
              clearTimeout(client.options.noPingReceivedTimeout);
              client.options.noPingReceivedTimeout = setTimeout(() => {
                client.disconnect();
              }, this.config.options.clientTimeout || 10000);
            }
          } else if (payload[0] === PROTOCOLS.CLIENT_CLOSE) {
            //console.log('HANDLING CLIENT CLOSED')
            this.removeClient(privateId);
          } else {
           // console.log('GOT MESSAGE!!!', { ack, seq, payload });
            if(ack) {
              if(client.options.receivedAcks.includes(ack)) {
                return;
              } else {
                client.options.receivedAcks.push(ack);
                if(seq) {
                  if(seq === client.options.lastProcessedSeq + 1) {
                    client.options.messageHandler?.(payload);
                    client.options.lastProcessedSeq = seq;
                    let nextSeq = seq + 1;
                    while(client.options.receivedOutOfOrderSeqs[nextSeq]) {
                      client.options.lastProcessedSeq = nextSeq;
                      const nextPayload = client.options.receivedOutOfOrderSeqs[nextSeq];
                      delete client.options.receivedOutOfOrderSeqs[nextSeq];
                      client.options.messageHandler?.(nextPayload);
                      nextSeq++;
                    }
                  } else {
                    client.options.receivedOutOfOrderSeqs[seq] = payload;
                  }
                } else {
                  client.options.messageHandler?.(payload);
                }
                return;
              }
            } else {
              client.options.messageHandler?.(payload);
            }
          }
        } else {
          throw new Error(`Could not validate client on host: ${host}:${port}`);
        }
      } catch (err) {
        console.error(`Error processing message: ${err instanceof Error ? err.message : String(err)}`);
      }
    });

    // Bind the UDP server to the provided port and address.
    this.server.bind(this.config.options.udpOptions?.port, this.listenHost(this.config.options.udpOptions?.host), () => {
      // console.log(`UDP server started on ${this.config.options.udpOptions.host}:${this.config.options.udpOptions.port}`);
    });
  }

  public async close() {
    if (this.server) {
      this.clients.forEach((client) => {
        delete this.clientsByPrivateId[client.privateId];
        client.disconnect();
      });
      this.clients.length = 0;
      
      return new Promise<void>((resolve) => {
        this.server.close(() => {
          //console.log('UDP server closed');
          resolve();
        });
      });
    }
  }
}
